export type StoreMutationDiet = "all" | "carnivore" | "herbivore";

export interface StoreMutationDefinition {
  name: string;
  description: string;
  diet: StoreMutationDiet;
  species?: readonly string[];
}

export const mutationSlotFields = [
  "MutationSlot1", "MutationSlot2", "MutationSlot3", "MutationSlot4",
  "ParentMutationSlot1", "ParentMutationSlot2", "ParentMutationSlot3", "ParentMutationSlot4",
  "ElderMutationSlot1A", "ElderMutationSlot1B", "ElderMutationSlot2A", "ElderMutationSlot2B",
  "ElderMutationSlot3A", "ElderMutationSlot3B", "ElderMutationSlot4A", "ElderMutationSlot4B"
] as const;

export type MutationSlotField = typeof mutationSlotFields[number];

export function compactMutationSlots(slots: readonly string[]): string[] {
  return slots.slice(0, mutationSlotFields.length).map((value) => value.trim()).filter(Boolean);
}

export function normalizeMutationSlots(slots: readonly string[] | undefined): string[] {
  return mutationSlotFields.map((_, index) => slots?.[index]?.trim() ?? "");
}

export function mutationSlotsFromFieldRecord(value: unknown): string[] | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  return mutationSlotFields.map((field) => typeof record[field] === "string" ? record[field].trim() : "");
}

// Verified against the current 0.21.720 client strings. Store purchases use the
// ordinary lifecycle pool only: unlock-required, female-only, horde-test, and
// slot-exclusive mutations remain choices the game owns.
export const storeMutationCatalog: readonly StoreMutationDefinition[] = [
  { name: "Cellular Regeneration", description: "Recover health slightly faster.", diet: "all" },
  { name: "Congenital Hypoalgesia", description: "Reduce incoming damage when fighting larger species.", diet: "all" },
  { name: "Efficient Digestion", description: "Food drains more slowly.", diet: "all" },
  { name: "Enlarged meniscus", description: "Fall damage drains stamina before health.", diet: "all" },
  { name: "Epidermal Fibrosis", description: "Increase bleed resistance.", diet: "all" },
  { name: "Featherweight", description: "Footprints fade more quickly.", diet: "all" },
  { name: "Hydrodynamic", description: "Increase swimming speed.", diet: "all" },
  { name: "Hydroregenerative", description: "Recover health faster during rain.", diet: "all" },
  { name: "Increased Inspiratory Capacity", description: "Increase oxygen capacity.", diet: "all" },
  { name: "Infrasound Communication", description: "Make less noise while using chat.", diet: "all" },
  { name: "Nocturnal", description: "Recover health faster at night.", diet: "all" },
  { name: "Osteosclerosis", description: "Resist fracture damage.", diet: "all" },
  { name: "Multichambered Lungs", description: "Improves respiratory endurance.", diet: "all" },
  { name: "Tactile Endurance", description: "Convert incoming damage to stamina.", diet: "herbivore" },
  { name: "Photosynthetic Tissue", description: "Recover health faster during the day.", diet: "all" },
  { name: "Reabsorption", description: "Recover water during rain or in drinkable water.", diet: "all" },
  { name: "Sequential Hermaphroditism", description: "Allows the dinosaur to change sex.", diet: "all" },
  { name: "Submerged Optical Retention", description: "Increase underwater vision range.", diet: "all" },
  { name: "Sustained Hydration", description: "Water drains more slowly.", diet: "all" },
  { name: "Wader", description: "Reduce the movement penalty in shallow water.", diet: "all" },
  { name: "Accelerated Prey Drive", description: "Deal more damage to badly injured prey.", diet: "carnivore" },
  { name: "Cannibalistic", description: "Adds the same species as preferred prey where supported.", diet: "carnivore" },
  { name: "Hematophagy", description: "Restore some thirst while eating corpses.", diet: "carnivore" },
  { name: "Hemomania", description: "Deal more damage to bleeding targets.", diet: "carnivore" },
  { name: "Enhanced Digestion", description: "Extract more value from consumed food.", diet: "all" },
  { name: "Gastronomic Regeneration", description: "Game-supported digestive regeneration mutation.", diet: "all" },
  { name: "Hypermetabolic Inanition", description: "Changes hunger metabolism for carnivores.", diet: "carnivore" },
  { name: "Reniculate Kidneys", description: "Improves water conservation for supported species.", diet: "all", species: ["Austroraptor", "Deinosuchus", "Gallimimus", "Pteranodon", "Triceratops", "Tyrannosaurus"] },
  { name: "Reinforced Tendons", description: "Improves tendon resilience for agile species.", diet: "all", species: ["Austroraptor", "Beipiaosaurus", "Herrerasaurus", "Omniraptor", "Pachycephalosaurus", "Pteranodon", "Troodon"] },
  { name: "Osteophagic", description: "Allows supported species to benefit from eating bones.", diet: "carnivore", species: ["Tyrannosaurus"] },
  { name: "Prolific Reproduction", description: "Improves reproductive output for supported species.", diet: "carnivore", species: ["Austroraptor", "Omniraptor", "Pteranodon"] },
  { name: "Advanced Gestation", description: "Improves gestation for supported species.", diet: "carnivore", species: ["Omniraptor"] },
  { name: "Barometric Sensitivity", description: "Receive an indication before storms or droughts.", diet: "herbivore" },
  { name: "Hypervigilance", description: "Improve awareness while eating and drinking.", diet: "herbivore" },
  { name: "Heightened Ghrelin", description: "Changes appetite behaviour for supported herbivores.", diet: "herbivore", species: ["Diabloceratops", "Pachycephalosaurus"] },
  { name: "Social Behavior", description: "Supports herd-oriented behaviour.", diet: "herbivore", species: ["Kentrosaurus", "Stegosaurus"] },
  { name: "Parthenogenesis", description: "Allows supported species to reproduce without a mate.", diet: "all", species: ["Allosaurus", "Kentrosaurus"] },
  { name: "Photosynthetic Regeneration", description: "Regenerate stamina faster during the day.", diet: "herbivore" },
  { name: "Truculency", description: "Improve the chance of bucking latched attackers.", diet: "herbivore" },
  { name: "Xerocole Adaptation", description: "Gain some water while eating plants.", diet: "herbivore" }
] as const;

const carnivoreSpecies = new Set([
  "allosaurus",
  "austroraptor",
  "carnotaurus",
  "ceratosaurus",
  "deinonychus",
  "deinosuchus",
  "dilophosaurus",
  "herrerasaurus",
  "omniraptor",
  "pteranodon",
  "troodon",
  "tyrannosaurus",
  "utahraptor"
]);

// Beipiaosaurus and Gallimimus are omnivores in-game, but the current mutation
// pool gives them the plant-eater choices rather than the carnivore-only pool.
// Keep this explicit so a newly released or misspelled species fails closed
// instead of silently receiving every mutation tagged `all`.
const plantEaterMutationSpecies = new Set([
  "beipiaosaurus",
  "diabloceratops",
  "dryosaurus",
  "gallimimus",
  "hypsilophodon",
  "kentrosaurus",
  "maiasaura",
  "pachycephalosaurus",
  "stegosaurus",
  "tenontosaurus",
  "triceratops"
]);

export function availableStoreMutationsForSpecies(species: string): readonly StoreMutationDefinition[] {
  const normalized = species.trim().toLowerCase();
  const diet: Exclude<StoreMutationDiet, "all"> | undefined = carnivoreSpecies.has(normalized)
    ? "carnivore"
    : plantEaterMutationSpecies.has(normalized)
      ? "herbivore"
      : undefined;
  if (!diet) return [];
  return storeMutationCatalog.filter((mutation) =>
    mutation.name !== "Social Behavior"
    &&
    (mutation.diet === "all" || mutation.diet === diet)
    && (!mutation.species || mutation.species.some((candidate) => candidate.toLowerCase() === normalized))
  );
}

export function validateStoreMutationSelection(species: string, mutations: readonly string[]): string[] {
  if (mutations.length !== 3) {
    throw new Error("Choose exactly three mutations for this dinosaur.");
  }
  const normalized = mutations.map((mutation) => mutation.trim());
  if (new Set(normalized.map((mutation) => mutation.toLowerCase())).size !== 3) {
    throw new Error("Choose three different mutations.");
  }
  const available = new Set(availableStoreMutationsForSpecies(species).map((mutation) => mutation.name));
  if (normalized.some((mutation) => !available.has(mutation))) {
    throw new Error(`One or more selected mutations are not available for ${species}.`);
  }
  return normalized;
}

export function validateSpecimenEggMutationSelection(
  species: string,
  mutations: readonly string[],
  requiredCount: number
): string[] {
  if (![4, 8, 12, 16].includes(requiredCount)) {
    throw new Error("That Specimen Egg tier is not supported.");
  }
  if (mutations.length !== requiredCount) {
    throw new Error(`Choose exactly ${requiredCount} mutations for this specimen.`);
  }
  const normalized = mutations.map((mutation) => mutation.trim());
  if (new Set(normalized.map((mutation) => mutation.toLowerCase())).size !== requiredCount) {
    throw new Error(`Choose ${requiredCount} different mutations.`);
  }
  const available = new Set(availableStoreMutationsForSpecies(species).map((mutation) => mutation.name));
  if (normalized.some((mutation) => !available.has(mutation))) {
    throw new Error(`One or more selected mutations are not available for ${species}.`);
  }
  return normalized;
}

export function validateMutationCorrection(
  species: string,
  existingMutations: readonly string[],
  requestedMutation: string
): string {
  if (existingMutations.length >= 16) {
    throw new Error("This dinosaur already has the maximum of 16 recorded mutations.");
  }

  const requested = requestedMutation.trim().toLowerCase();
  const canonical = availableStoreMutationsForSpecies(species)
    .find((mutation) => mutation.name.toLowerCase() === requested)?.name;
  if (!canonical) {
    throw new Error(`That mutation is not available for ${species}.`);
  }

  if (existingMutations.some((mutation) => mutation.trim().toLowerCase() === canonical.toLowerCase())) {
    throw new Error(`${canonical} is already recorded on this dinosaur.`);
  }

  return canonical;
}

export function validateMutationReplacement(
  species: string,
  existingMutations: readonly string[],
  mutationToReplace: string,
  requestedMutation: string
): { replaceMutation: string; mutation: string } {
  const replaceRequested = mutationToReplace.trim().toLowerCase();
  const replaceMutation = existingMutations.find((mutation) => mutation.trim().toLowerCase() === replaceRequested);
  if (!replaceMutation) {
    throw new Error("The mutation selected for replacement is no longer recorded on this dinosaur.");
  }

  const requested = requestedMutation.trim().toLowerCase();
  const mutation = availableStoreMutationsForSpecies(species)
    .find((candidate) => candidate.name.toLowerCase() === requested)?.name;
  if (!mutation) {
    throw new Error(`That mutation is not available for ${species}.`);
  }
  if (mutation.toLowerCase() === replaceMutation.trim().toLowerCase()) {
    throw new Error("Choose a different replacement mutation.");
  }

  const remaining = [...existingMutations];
  remaining.splice(remaining.indexOf(replaceMutation), 1);
  if (remaining.some((existing) => existing.trim().toLowerCase() === mutation.toLowerCase())) {
    throw new Error(`${mutation} is already recorded in another mutation slot on this dinosaur.`);
  }
  return { replaceMutation, mutation };
}

export function validateMutationSlotCorrection(
  species: string,
  mutationSlots: readonly string[],
  targetSlot: MutationSlotField,
  requestedMutation: string
): { targetSlot: MutationSlotField; expectedMutation: string; mutation: string } {
  const slotIndex = mutationSlotFields.indexOf(targetSlot);
  if (slotIndex < 0) {
    throw new Error("Choose one of the dinosaur's 16 mutation slots.");
  }
  const slots = normalizeMutationSlots(mutationSlots);
  const expectedMutation = slots[slotIndex] ?? "";
  const requested = requestedMutation.trim().toLowerCase();
  const mutation = availableStoreMutationsForSpecies(species)
    .find((candidate) => candidate.name.toLowerCase() === requested)?.name;
  if (!mutation) {
    throw new Error(`That mutation is not available for ${species}.`);
  }
  if (mutation.toLowerCase() === expectedMutation.toLowerCase()) {
    throw new Error("Choose a different mutation for this slot.");
  }
  const existingSlotIndex = slots.findIndex((existing, index) => index !== slotIndex && existing.toLowerCase() === mutation.toLowerCase());
  if (existingSlotIndex >= 0) {
    throw new Error(`${mutation} already exists in ${mutationSlotFields[existingSlotIndex]}.`);
  }
  return { targetSlot, expectedMutation, mutation };
}
